import { Pencil, Plus } from "lucide-react";
import { RecordDrawer } from "@/components/RecordDrawer";
import { VENDOR_FIELDS } from "@/lib/records";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Badge, Button, Card, Empty, ErrorNote, inputClass, Spinner } from "@/components/ui";
import { useToast } from "@/components/Toast";
import { api, type VendorRow, type VendorUpsert } from "@/lib/api";
import { ENGAGEMENT_SERVICES, humanise, VENDOR_TYPES } from "@/lib/dealStages";
import { formatDate, formatMoneyCents, formatPhone } from "@/lib/format";
import { cn } from "@/lib/utils";

function RateCardEditor({ vendor }: { vendor: VendorRow }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [text, setText] = useState(() => JSON.stringify(vendor.rate_card, null, 2));
  const [invalid, setInvalid] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (rateCard: Record<string, unknown>) =>
      api.updateVendor(vendor.id, {
        type: vendor.type,
        name: vendor.name,
        company: vendor.company,
        phone_e164: vendor.phone_e164,
        email: vendor.email,
        website: vendor.website,
        states: vendor.states,
        counties: vendor.counties,
        license_no: vendor.license_no,
        rate_card: rateCard,
        typical_turnaround_days: vendor.typical_turnaround_days,
        rating: vendor.rating,
        active: vendor.active,
        notes: vendor.notes,
      }),
    onSuccess: () => {
      toast.push(`${vendor.name}'s rate card saved`);
      void qc.invalidateQueries();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });

  return (
    <div>
      <textarea
        value={text}
        rows={8}
        onChange={(e) => {
          setText(e.target.value);
          try {
            JSON.parse(e.target.value);
            setInvalid(null);
          } catch (err) {
            setInvalid(err instanceof Error ? err.message : "not valid JSON");
          }
        }}
        className={inputClass + " font-mono text-[11px]"}
        data-testid="rate-card-editor"
      />
      {invalid && <p className="mt-1 text-[11px] text-danger">{invalid}</p>}
      <div className="mt-1 flex items-center gap-2">
        <Button
          size="sm"
          disabled={Boolean(invalid) || save.isPending}
          onClick={() => save.mutate(JSON.parse(text) as Record<string, unknown>)}
          data-testid="rate-card-save"
        >
          Save rate card
        </Button>
        <span className="text-[11px] text-fg-muted">
          Amounts in cents. Keys ending <code>_cents</code> feed §6.7 estimates.
        </span>
      </div>
    </div>
  );
}

/** §7.6 vendor directory and detail. */
export function Vendors() {
  const [type, setType] = useState("");
  const [state, setState] = useState("");
  const [service, setService] = useState("");
  const [q, setQ] = useState("");
  const [onlyActive, setOnlyActive] = useState(true);
  const [openId, setOpenId] = useState<string | null>(null);
  // v2.1 T6.1: "Add vendor" and the edit, in one drawer; null = closed, "new" = add.
  const [drawer, setDrawer] = useState<VendorRow | "new" | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["vendors", { type, state, service, q, onlyActive }],
    queryFn: () =>
      api.vendors({
        type: type ? [type] : undefined,
        state: state || undefined,
        service: service || undefined,
        q: q || undefined,
        active: onlyActive ? true : undefined,
      }),
  });

  return (
    <div className="p-4">
      <header className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">Vendors</h1>
          <p className="text-xs text-fg-muted">
            {data ? `${data.length} vendor${data.length === 1 ? "" : "s"}` : "…"} · on-time % and
            turnaround are computed from engagement history
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Name or company…"
            className={inputClass + " w-44"}
            data-testid="vendor-filter"
          />
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className={inputClass + " w-44"}
            aria-label="Filter by vendor type"
          >
            <option value="">All types</option>
            {VENDOR_TYPES.map((t) => (
              <option key={t} value={t}>
                {humanise(t)}
              </option>
            ))}
          </select>
          <select
            value={state}
            onChange={(e) => setState(e.target.value)}
            className={inputClass + " w-24"}
            aria-label="Filter by state"
          >
            <option value="">State</option>
            {["TX", "TN", "AL"].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select
            value={service}
            onChange={(e) => setService(e.target.value)}
            className={inputClass + " w-44"}
            aria-label="Has done this service"
            data-testid="vendor-service-filter"
          >
            <option value="">Any service done</option>
            {ENGAGEMENT_SERVICES.map((s) => (
              <option key={s} value={s}>
                {humanise(s)}
              </option>
            ))}
          </select>
          <Button
            size="sm"
            variant={onlyActive ? "default" : "ghost"}
            onClick={() => setOnlyActive((v) => !v)}
          >
            {onlyActive ? "Active only" : "All"}
          </Button>
          <Button size="sm" variant="primary" data-testid="vendor-add" onClick={() => setDrawer("new")}>
            <Plus className="size-3.5" aria-hidden /> Add vendor
          </Button>
        </div>
      </header>

      {drawer && (
        <RecordDrawer
          title={drawer === "new" ? "Add vendor" : `Edit ${drawer.name}`}
          fields={VENDOR_FIELDS}
          initial={drawer === "new" ? { type: "surveyor", active: true } : drawer}
          submitLabel={drawer === "new" ? "Add vendor" : "Save"}
          testId="vendor-drawer"
          invalidate={["vendors"]}
          onClose={() => setDrawer(null)}
          onSubmit={(values) => {
            const body = {
              ...(values as VendorUpsert),
              rate_card: drawer === "new" ? {} : (drawer.rate_card ?? {}),
              license_no: drawer === "new" ? null : (drawer.license_no ?? null),
            };
            return drawer === "new" ? api.createVendor(body) : api.updateVendor(drawer.id, body);
          }}
        />
      )}

      {isLoading && <Spinner />}
      {error && <ErrorNote error={error} />}

      {data && (
        <Card className="overflow-hidden">
          {data.length === 0 ? (
            <Empty title="No vendors match." />
          ) : (
            <div className="-mx-3 -my-3 overflow-x-auto">
              <table className="w-full text-sm" data-testid="vendors-table">
                <thead>
                  <tr className="border-b bg-surface-2 text-left text-xs font-semibold">
                    <th className="px-2 py-1.5">Name</th>
                    <th className="px-2 py-1.5">Type</th>
                    <th className="px-2 py-1.5">Coverage</th>
                    <th className="px-2 py-1.5">Phone</th>
                    <th className="px-2 py-1.5 text-right">Rating</th>
                    <th className="px-2 py-1.5 text-right">Jobs</th>
                    <th className="px-2 py-1.5 text-right">On time</th>
                    <th className="px-2 py-1.5 text-right">Avg days</th>
                    <th className="px-2 py-1.5 text-right">Promised</th>
                    <th className="px-2 py-1.5 text-right">Paid</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((v) => (
                    <>
                      <tr
                        key={v.id}
                        className={cn(
                          "cursor-pointer border-b last:border-b-0 hover:bg-surface-2",
                          !v.active && "opacity-60",
                        )}
                        onClick={() => setOpenId(openId === v.id ? null : v.id)}
                        data-testid="vendor-row"
                      >
                        <td className="px-2 py-1.5">
                          <span className="font-medium">{v.name}</span>
                          {v.company && (
                            <span className="block text-xs text-fg-muted">{v.company}</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-xs">{humanise(v.type)}</td>
                        <td className="px-2 py-1.5 text-xs text-fg-muted">
                          {v.states.join(", ")}
                          {v.counties.length > 0 && ` · ${v.counties.join(", ")}`}
                        </td>
                        <td className="px-2 py-1.5 tabular-nums text-fg-muted">
                          {formatPhone(v.phone_e164)}
                        </td>
                        <td className="px-2 py-1.5 text-right">{v.rating ?? "—"}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">
                          {v.engagement_count}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums">
                          {v.on_time_pct === null ? "—" : `${v.on_time_pct}%`}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums">
                          {v.avg_turnaround_days ?? "—"}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums text-fg-muted">
                          {v.typical_turnaround_days ?? "—"}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums">
                          {formatMoneyCents(v.total_paid_cents)}
                        </td>
                      </tr>
                      {openId === v.id && (
                        <tr key={`${v.id}-detail`} className="border-b bg-surface-2">
                          <td colSpan={10} className="px-3 py-3">
                            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                              <div>
                                <h3 className="mb-1 text-xs font-semibold">Rate card</h3>
                                <RateCardEditor vendor={v} />
                              </div>
                              <div className="text-xs">
                                <h3 className="mb-1 flex items-center justify-between font-semibold">
                                  Detail
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    data-testid="vendor-edit"
                                    onClick={() => setDrawer(v)}
                                  >
                                    <Pencil className="size-3.5" aria-hidden /> Edit
                                  </Button>
                                </h3>
                                <dl className="space-y-0.5">
                                  <div className="flex justify-between gap-2">
                                    <dt className="text-fg-muted">Email</dt>
                                    <dd>{v.email ?? "—"}</dd>
                                  </div>
                                  <div className="flex justify-between gap-2">
                                    <dt className="text-fg-muted">Licence</dt>
                                    <dd>{v.license_no ?? "—"}</dd>
                                  </div>
                                  <div className="flex justify-between gap-2">
                                    <dt className="text-fg-muted">Delivered jobs</dt>
                                    <dd className="tabular-nums">{v.delivered_count}</dd>
                                  </div>
                                  <div className="flex justify-between gap-2">
                                    <dt className="text-fg-muted">Last contacted</dt>
                                    <dd>{formatDate(v.last_contacted_at)}</dd>
                                  </div>
                                  <div className="flex justify-between gap-2">
                                    <dt className="text-fg-muted">Active</dt>
                                    <dd>
                                      <Badge tone={v.active ? "ok" : "muted"}>
                                        {v.active ? "yes" : "no"}
                                      </Badge>
                                    </dd>
                                  </div>
                                </dl>
                                {v.notes && <p className="mt-2 text-fg-muted">{v.notes}</p>}
                                {v.avg_turnaround_days !== null &&
                                  v.typical_turnaround_days !== null && (
                                    <p className="mt-2 text-[11px] text-fg-muted">
                                      Averaging {v.avg_turnaround_days} days against a promised{" "}
                                      {v.typical_turnaround_days}.
                                    </p>
                                  )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
